/**
 * Offering a waiting service worker, rather than never activating it.
 *
 * ## The bug this fixes
 *
 * `vite.config.ts` sets `registerType: 'prompt'`, which builds a worker that
 * calls `skipWaiting()` only when it is sent a `SKIP_WAITING` message. Nothing
 * sent one. So a new release installed, entered the `waiting` state, and stayed
 * there: a waiting worker activates only once every tab in scope has closed, and
 * an installed standalone PWA is exactly the kind of app people never close.
 *
 * Users therefore stayed on whatever version they first installed. That matters
 * more here than in most apps for a specific reason — the peer set is bounded at
 * build time (`relays.source.json`), so adding a relay is a release. A release
 * mechanism nobody receives is not a release mechanism, and the same silence
 * would have swallowed a security fix.
 *
 * ## Why a prompt and not `autoUpdate`
 *
 * `autoUpdate` would have been one line. It also reloads the page underneath
 * whoever is using it, and this app has screens that hold unsaved input: a note
 * being typed, a passphrase being entered on the sync consent flow. Discarding
 * that to install an update a minute sooner is the wrong trade.
 *
 * So the choice stays with the user, and the copy says plainly that the update
 * lands on the next reload either way — ignoring this notice delays the new
 * version, it does not decline it.
 */
import { t } from '@copy/index'
import { Notice } from '@shared/ui'
import { useRegisterSW } from 'virtual:pwa-register/react'

export function UpdateNotice() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW()

  /*
   * No dismiss control, on purpose.
   *
   * `Notice` carries one action, and reloading is the one worth having. A second
   * "Later" button would need somewhere to remember the refusal, and remembering
   * it means storing which version was declined — another record on a device
   * where every record is a liability, to suppress a single line of text that
   * already says the update can wait for the next reload.
   */
  if (!needRefresh) return null

  return (
    <div className="px-4 pt-4">
      <Notice
        tone="info"
        action={{
          label: t('update.action'),
          // `true` reloads once the new worker has taken control. Reloading
          // ourselves instead would race it and could load the old assets again.
          onClick: () => void updateServiceWorker(true),
        }}
      >
        {t('update.available')}
      </Notice>
    </div>
  )
}
